import { useId, useState } from 'react';
import { MediaAssetPicker } from '../../marketing/components/media-asset-picker.js';
import type { AdminBrand } from '../types/catalog.js';
import type { CreateBrandInput } from '../api/catalog-api.js';

export interface BrandFormProps {
  initial?: AdminBrand;
  onSubmit: (input: CreateBrandInput) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function BrandForm({ initial, onSubmit, onCancel, isSubmitting }: BrandFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [logoMediaAssetId, setLogoMediaAssetId] = useState(initial?.logoMediaAssetId ?? '');
  const [bannerMediaAssetId, setBannerMediaAssetId] = useState(initial?.bannerMediaAssetId ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [manufacturer, setManufacturer] = useState(initial?.manufacturer ?? '');
  const [countryOfOrigin, setCountryOfOrigin] = useState(initial?.countryOfOrigin ?? '');
  const [supplier, setSupplier] = useState(initial?.supplier ?? '');
  const [website, setWebsite] = useState(initial?.website ?? '');
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  const [seoTitle, setSeoTitle] = useState(initial?.seoTitle ?? '');
  const [seoMetaDescription, setSeoMetaDescription] = useState(initial?.seoMetaDescription ?? '');
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();
  const manufacturerId = useId();
  const countryOfOriginId = useId();
  const supplierId = useId();
  const websiteId = useId();
  const contactEmailId = useId();
  const contactPhoneId = useId();
  const descriptionId = useId();
  const seoTitleId = useId();
  const seoMetaDescriptionId = useId();

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setError(null);
    onSubmit({
      name: name.trim(),
      logoMediaAssetId: logoMediaAssetId.trim() || null,
      bannerMediaAssetId: bannerMediaAssetId.trim() || null,
      description: description.trim() || null,
      manufacturer: manufacturer.trim() || null,
      countryOfOrigin: countryOfOrigin.trim() || null,
      supplier: supplier.trim() || null,
      website: website.trim() || null,
      contactEmail: contactEmail.trim() || null,
      contactPhone: contactPhone.trim() || null,
      seoTitle: seoTitle.trim() || null,
      seoMetaDescription: seoMetaDescription.trim() || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
      <div>
        <label htmlFor={nameId} className="mb-1 block text-xs font-medium text-gray-600">
          Name
        </label>
        <input
          id={nameId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      <div>
        <label htmlFor={descriptionId} className="mb-1 block text-xs font-medium text-gray-600">
          Description
        </label>
        <textarea
          id={descriptionId}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MediaAssetPicker label="Brand logo" value={logoMediaAssetId} onChange={setLogoMediaAssetId} />
        <MediaAssetPicker label="Brand banner" value={bannerMediaAssetId} onChange={setBannerMediaAssetId} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={manufacturerId} className="mb-1 block text-xs font-medium text-gray-600">
            Manufacturer
          </label>
          <input
            id={manufacturerId}
            value={manufacturer}
            onChange={(event) => setManufacturer(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label htmlFor={countryOfOriginId} className="mb-1 block text-xs font-medium text-gray-600">
            Country of origin
          </label>
          <input
            id={countryOfOriginId}
            value={countryOfOrigin}
            onChange={(event) => setCountryOfOrigin(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={supplierId} className="mb-1 block text-xs font-medium text-gray-600">
            Supplier
          </label>
          <input
            id={supplierId}
            value={supplier}
            onChange={(event) => setSupplier(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label htmlFor={websiteId} className="mb-1 block text-xs font-medium text-gray-600">
            Website
          </label>
          <input
            id={websiteId}
            type="url"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
            placeholder="https://example.com"
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={contactEmailId} className="mb-1 block text-xs font-medium text-gray-600">
            Contact email
          </label>
          <input
            id={contactEmailId}
            type="email"
            value={contactEmail}
            onChange={(event) => setContactEmail(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label htmlFor={contactPhoneId} className="mb-1 block text-xs font-medium text-gray-600">
            Contact phone
          </label>
          <input
            id={contactPhoneId}
            type="tel"
            value={contactPhone}
            onChange={(event) => setContactPhone(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
      </div>

      <div>
        <label htmlFor={seoTitleId} className="mb-1 block text-xs font-medium text-gray-600">
          SEO title
        </label>
        <input
          id={seoTitleId}
          value={seoTitle}
          onChange={(event) => setSeoTitle(event.target.value)}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      <div>
        <label htmlFor={seoMetaDescriptionId} className="mb-1 block text-xs font-medium text-gray-600">
          SEO meta description
        </label>
        <textarea
          id={seoMetaDescriptionId}
          value={seoMetaDescription}
          onChange={(event) => setSeoMetaDescription(event.target.value)}
          rows={3}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-brand text-brand-foreground rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-60"
        >
          {isSubmitting ? 'Saving…' : 'Save brand'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
